import { create } from 'zustand'
import type { Cell, CellRange, CellStyle, Sheet, SheetImage, Workbook } from './types'
import { DEFAULT_COL_WIDTH, DEFAULT_ROW_HEIGHT } from './types'
import { colName, forEachCell, normalizeRange, offsetFormula, ref, shiftFormula } from './a1'
import { createEvaluator, parseInput } from './engine'
import { expandForMerges, mergeAt, rangesIntersect, shiftMergesOnDelete, shiftMergesOnInsert } from './merges'

export interface Pos {
  row: number
  col: number
}

let sheetSeq = 0
const newId = () => `sh${++sheetSeq}_${Math.random().toString(36).slice(2, 7)}`

export function blankSheet(name: string, rows = 200, cols = 26): Sheet {
  return { id: newId(), name, cells: {}, rows, cols, colWidths: {}, rowHeights: {} }
}

function blankWorkbook(): Workbook {
  const s = blankSheet('Sheet1')
  return { sheets: [s], activeSheetId: s.id }
}

interface UndoEntry {
  label: string
  group?: string
  wb: Workbook
  sel: Selection
}

export interface WorkbookChange {
  revision: number
  at: number
  actor: 'user' | 'ai'
  label: string
  summary: string
}

export interface Selection {
  sheetId: string
  anchor: Pos
  focus: Pos
}

export interface Editing {
  row: number
  col: number
  value: string
  /** true = 셀 위에서 바로 타이핑 시작(기존 내용 대체) */
  replace: boolean
}

interface State {
  wb: Workbook
  version: number
  sel: Selection
  editing: Editing | null
  undoStack: UndoEntry[]
  redoStack: UndoEntry[]
  /** 사용자와 AI가 통합문서에 가한 최근 변경. 다음 AI 턴의 문맥으로 전달된다. */
  recentChanges: WorkbookChange[]
  /** "sheetId!A1" → 하이라이트 만료 시각(ms). AI 가 방금 바꾼 셀 표시용 */
  aiTouched: Record<string, number>
  fileName: string
  statusMsg: string
}

interface Actions {
  activeSheet: () => Sheet
  sheetByName: (name?: string) => Sheet
  selRange: () => CellRange

  select: (anchor: Pos, focus?: Pos) => void
  setEditing: (e: Editing | null) => void
  setStatus: (m: string) => void

  commitEdit: (value: string, move?: 'down' | 'right' | 'none') => void
  setCellsRaw: (
    sheetId: string,
    entries: Array<{ row: number; col: number; input: string }>,
    label: string,
    group?: string
  ) => void
  clearRange: (sheetId: string, r: CellRange, what: 'all' | 'contents' | 'formats', group?: string) => void
  formatRange: (sheetId: string, r: CellRange, patch: CellStyle, group?: string) => void
  fillRange: (sheetId: string, source: CellRange, target: CellRange, group?: string) => void
  mergeRange: (sheetId: string, r: CellRange, group?: string) => void
  unmergeRange: (sheetId: string, r: CellRange, group?: string) => void
  insertRows: (sheetId: string, at: number, count: number, group?: string) => void
  deleteRows: (sheetId: string, at: number, count: number, group?: string) => void
  insertCols: (sheetId: string, at: number, count: number, group?: string) => void
  deleteCols: (sheetId: string, at: number, count: number, group?: string) => void
  sortRange: (
    sheetId: string,
    r: CellRange,
    byCol: number,
    order: 'asc' | 'desc',
    hasHeader: boolean,
    group?: string
  ) => void
  findReplace: (
    sheetId: string | null,
    find: string,
    replace: string,
    matchCase: boolean,
    group?: string
  ) => number
  setColWidth: (sheetId: string, col: number, px: number) => void
  setRowHeight: (sheetId: string, row: number, px: number) => void
  ensureSize: (sheetId: string, rows: number, cols: number) => void
  addImage: (sheetId: string, image: SheetImage, group?: string) => void
  updateImage: (sheetId: string, imageId: string, patch: Partial<SheetImage>, group?: string) => void
  removeImage: (sheetId: string, imageId: string, group?: string) => void

  addSheet: (name?: string, group?: string) => Sheet
  applyTemplate: (template: Sheet, mode: 'new' | 'replace') => Sheet
  renameSheet: (sheetId: string, name: string, group?: string) => void
  deleteSheet: (sheetId: string, group?: string) => void
  setActiveSheet: (sheetId: string) => void

  loadWorkbook: (wb: Workbook, fileName: string) => void
  markAi: (keys: string[]) => void

  undo: () => void
  redo: () => void
}

export type Store = State & Actions

// ── 내부 헬퍼 ────────────────────────────────────────────────────────────────

function clone<T>(v: T): T {
  return structuredClone(v)
}

function findSheet(wb: Workbook, id: string): Sheet {
  const s = wb.sheets.find((x) => x.id === id)
  if (!s) throw new Error(`시트를 찾을 수 없습니다: ${id}`)
  return s
}

function setCell(sheet: Sheet, row: number, col: number, patch: Partial<Cell> | null) {
  const key = ref(row, col)
  if (patch === null) {
    delete sheet.cells[key]
    return
  }
  const cur = sheet.cells[key] ?? {}
  const next: Cell = { ...cur, ...patch }
  if ('f' in patch && patch.f === undefined) delete next.f
  if ('v' in patch && patch.v === undefined) delete next.v
  const empty =
    next.f === undefined &&
    (next.v === undefined || next.v === null || next.v === '') &&
    (!next.s || Object.keys(next.s).length === 0)
  if (empty) delete sheet.cells[key]
  else sheet.cells[key] = next
}

function growTo(sheet: Sheet, row: number, col: number) {
  if (row > sheet.rows) sheet.rows = Math.min(1_048_576, row + 20)
  if (col > sheet.cols) sheet.cols = Math.min(16_384, col + 3)
}

/** 데이터가 실제로 들어있는 최대 행/열 */
export function usedRange(sheet: Sheet): CellRange | null {
  let r2 = 0
  let c2 = 0
  for (const key of Object.keys(sheet.cells)) {
    const m = /^([A-Z]{1,3})([0-9]+)$/.exec(key)
    if (!m) continue
    const row = Number(m[2])
    let col = 0
    for (let i = 0; i < m[1].length; i++) col = col * 26 + (m[1].charCodeAt(i) - 64)
    if (row > r2) r2 = row
    if (col > c2) c2 = col
  }
  return r2 && c2 ? { r1: 1, c1: 1, r2, c2 } : null
}

/** 시트 전체 셀 주소를 (row, col) 로 훑는다. */
function eachCellEntry(sheet: Sheet, fn: (row: number, col: number, cell: Cell, key: string) => void) {
  for (const [key, cell] of Object.entries(sheet.cells)) {
    const m = /^([A-Z]{1,3})([0-9]+)$/.exec(key)
    if (!m) continue
    let col = 0
    for (let i = 0; i < m[1].length; i++) col = col * 26 + (m[1].charCodeAt(i) - 64)
    fn(Number(m[2]), col, cell, key)
  }
}

function remapAllFormulas(wb: Workbook, mapper: (f: string) => string) {
  for (const s of wb.sheets) for (const c of Object.values(s.cells)) if (c.f) c.f = mapper(c.f)
}

function workbookChangeSummary(before: Workbook, after: Workbook): string {
  const details: string[] = []
  const beforeById = new Map(before.sheets.map((s) => [s.id, s]))
  const afterById = new Map(after.sheets.map((s) => [s.id, s]))

  for (const sheet of after.sheets) {
    const old = beforeById.get(sheet.id)
    if (!old) {
      details.push(`${sheet.name}: 시트 추가`)
      continue
    }
    const keys = new Set([...Object.keys(old.cells), ...Object.keys(sheet.cells)])
    const changed: string[] = []
    for (const key of keys) {
      if (JSON.stringify(old.cells[key]) !== JSON.stringify(sheet.cells[key])) changed.push(key)
    }
    const extras: string[] = []
    if (old.name !== sheet.name) extras.push(`이름 ${old.name}→${sheet.name}`)
    if (JSON.stringify(old.merges ?? []) !== JSON.stringify(sheet.merges ?? [])) extras.push('병합')
    if (JSON.stringify(old.colWidths) !== JSON.stringify(sheet.colWidths)) extras.push('열 너비')
    if (JSON.stringify(old.rowHeights) !== JSON.stringify(sheet.rowHeights)) extras.push('행 높이')
    if (JSON.stringify(old.images ?? []) !== JSON.stringify(sheet.images ?? [])) extras.push('이미지')
    if (old.rows !== sheet.rows || old.cols !== sheet.cols) extras.push(`크기 ${sheet.rows}행×${sheet.cols}열`)
    if (old.frozenRows !== sheet.frozenRows) extras.push('고정 행')
    if (changed.length) {
      const sample = changed.slice(0, 12).join(',')
      extras.unshift(`셀 ${sample}${changed.length > 12 ? ` 외 ${changed.length - 12}개` : ''}`)
    }
    if (extras.length) details.push(`${sheet.name}: ${extras.join(', ')}`)
  }
  for (const sheet of before.sheets) {
    if (!afterById.has(sheet.id)) details.push(`${sheet.name}: 시트 삭제`)
  }
  return details.join(' / ') || '통합문서 메타데이터 변경'
}

// ── 스토어 ───────────────────────────────────────────────────────────────────

export const useStore = create<Store>((set, get) => {
  /** 모든 변형의 단일 통로: 스냅샷 → 클론 편집 → 커밋 */
  function mutate(label: string, group: string | undefined, fn: (wb: Workbook) => void) {
    const st = get()
    const prev: UndoEntry = { label, group, wb: st.wb, sel: st.sel }
    const next = clone(st.wb)
    fn(next)
    const revision = st.version + 1
    const change: WorkbookChange = {
      revision,
      at: Date.now(),
      actor: group?.startsWith('ai:') ? 'ai' : 'user',
      label,
      summary: workbookChangeSummary(st.wb, next),
    }
    const top = st.undoStack[st.undoStack.length - 1]
    const skipPush = Boolean(group && top && top.group === group)
    set({
      wb: next,
      version: revision,
      undoStack: skipPush ? st.undoStack : [...st.undoStack, prev].slice(-120),
      redoStack: [],
      recentChanges: [...st.recentChanges, change].slice(-80),
    })
  }

  return {
    wb: blankWorkbook(),
    version: 0,
    sel: { sheetId: '', anchor: { row: 1, col: 1 }, focus: { row: 1, col: 1 } },
    editing: null,
    undoStack: [],
    redoStack: [],
    recentChanges: [],
    aiTouched: {},
    fileName: 'untitled.xlsx',
    statusMsg: '',

    activeSheet: () => {
      const { wb } = get()
      return wb.sheets.find((s) => s.id === wb.activeSheetId) ?? wb.sheets[0]
    },
    sheetByName: (name) => {
      const { wb } = get()
      if (!name) return get().activeSheet()
      return (
        wb.sheets.find((s) => s.name.toLowerCase() === String(name).toLowerCase()) ?? get().activeSheet()
      )
    },
    selRange: () => {
      const { sel } = get()
      return normalizeRange({ r1: sel.anchor.row, c1: sel.anchor.col, r2: sel.focus.row, c2: sel.focus.col })
    },

    select: (anchor, focus) => {
      const sheet = get().activeSheet()
      const ma = mergeAt(sheet, anchor.row, anchor.col)
      const mf = mergeAt(sheet, (focus ?? anchor).row, (focus ?? anchor).col)
      set({
        sel: {
          sheetId: get().wb.activeSheetId,
          anchor: ma ? { row: ma.r1, col: ma.c1 } : anchor,
          focus: mf ? { row: mf.r2, col: mf.c2 } : (focus ?? (ma ? { row: ma.r2, col: ma.c2 } : anchor)),
        },
      })
    },
    setEditing: (e) => set({ editing: e }),
    setStatus: (m) => set({ statusMsg: m }),

    commitEdit: (value, move = 'down') => {
      const st = get()
      const e = st.editing
      if (!e) return
      const sheetId = st.wb.activeSheetId
      st.setCellsRaw(sheetId, [{ row: e.row, col: e.col, input: value }], `${ref(e.row, e.col)} 입력`)
      const nextPos =
        move === 'down'
          ? { row: e.row + 1, col: e.col }
          : move === 'right'
            ? { row: e.row, col: e.col + 1 }
            : { row: e.row, col: e.col }
      set({ editing: null })
      get().select(nextPos)
    },

    setCellsRaw: (sheetId, entries, label, group) => {
      mutate(label, group, (wb) => {
        const sheet = findSheet(wb, sheetId)
        for (const { row, col, input } of entries) {
          growTo(sheet, row, col)
          const parsed = parseInput(input)
          const key = ref(row, col)
          const style = sheet.cells[key]?.s
          if (parsed.f !== undefined) setCell(sheet, row, col, { f: parsed.f, v: undefined, s: style })
          else setCell(sheet, row, col, { v: parsed.v ?? null, f: undefined, s: style })
        }
      })
    },

    clearRange: (sheetId, r, what, group) => {
      mutate('지우기', group, (wb) => {
        const sheet = findSheet(wb, sheetId)
        forEachCell(r, (row, col) => {
          const key = ref(row, col)
          const cur = sheet.cells[key]
          if (!cur) return
          if (what === 'all') delete sheet.cells[key]
          else if (what === 'contents') setCell(sheet, row, col, { v: undefined, f: undefined })
          else setCell(sheet, row, col, { s: undefined })
        })
      })
    },

    formatRange: (sheetId, r, patch, group) => {
      mutate('서식', group, (wb) => {
        const sheet = findSheet(wb, sheetId)
        forEachCell(r, (row, col) => {
          growTo(sheet, row, col)
          const key = ref(row, col)
          const cur = sheet.cells[key] ?? {}
          const s: CellStyle = { ...(cur.s ?? {}) }
          for (const [k, v] of Object.entries(patch)) {
            if (v === undefined || v === null || v === '') delete (s as any)[k]
            else (s as any)[k] = v
          }
          sheet.cells[key] = { ...cur, s: Object.keys(s).length ? s : undefined }
          if (!sheet.cells[key].s && cur.v === undefined && cur.f === undefined) delete sheet.cells[key]
        })
      })
    },

    fillRange: (sheetId, source, target, group) => {
      const src = normalizeRange(source)
      const dst = normalizeRange(target)
      mutate('자동 채우기', group, (wb) => {
        const sheet = findSheet(wb, sheetId)
        if ((sheet.merges ?? []).some((m) => rangesIntersect(m, dst))) return
        const srcH = src.r2 - src.r1 + 1
        const srcW = src.c2 - src.c1 + 1
        const vertical = dst.c1 === src.c1 && dst.c2 === src.c2
        const horizontal = dst.r1 === src.r1 && dst.r2 === src.r2
        const seriesCells: Cell[] = []
        if (vertical && srcW === 1) for (let r = src.r1; r <= src.r2; r++) seriesCells.push(clone(sheet.cells[ref(r, src.c1)] ?? {}))
        if (horizontal && srcH === 1) for (let c = src.c1; c <= src.c2; c++) seriesCells.push(clone(sheet.cells[ref(src.r1, c)] ?? {}))
        const nums = seriesCells.map((c) => typeof c.v === 'number' ? c.v : NaN)
        const numericStep = nums.length >= 2 && nums.every(Number.isFinite) ? nums[1] - nums[0] : null
        const dates = seriesCells.map((c) => typeof c.v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(c.v) ? Date.parse(c.v) : NaN)
        const dateStep = dates.length >= 2 && dates.every(Number.isFinite) ? dates[1] - dates[0] : null

        forEachCell(dst, (row, col) => {
          if (row >= src.r1 && row <= src.r2 && col >= src.c1 && col <= src.c2) return
          const sr = src.r1 + ((row - src.r1) % srcH + srcH) % srcH
          const sc = src.c1 + ((col - src.c1) % srcW + srcW) % srcW
          const original = sheet.cells[ref(sr, sc)]
          if (!original) { delete sheet.cells[ref(row, col)]; return }
          const copy = clone(original)
          if (copy.f) copy.f = offsetFormula(copy.f, row - sr, col - sc)
          const index = vertical ? row - src.r1 : horizontal ? col - src.c1 : 0
          if (numericStep !== null && index >= seriesCells.length) copy.v = nums[0] + numericStep * index
          if (dateStep !== null && index >= seriesCells.length) copy.v = new Date(dates[0] + dateStep * index).toISOString().slice(0, 10)
          sheet.cells[ref(row, col)] = copy
          growTo(sheet, row, col)
        })
      })
      get().select({ row: dst.r1, col: dst.c1 }, { row: dst.r2, col: dst.c2 })
    },

    mergeRange: (sheetId, r, group) => {
      const sheet = get().wb.sheets.find((s) => s.id === sheetId)
      if (!sheet) return
      const n = expandForMerges(sheet, r)
      if (n.r1 === n.r2 && n.c1 === n.c2) return
      mutate('셀 병합', group, (wb) => {
        const target = findSheet(wb, sheetId)
        target.merges = [...(target.merges ?? []).filter((m) => !rangesIntersect(m, n)), n]
        forEachCell(n, (row, col) => {
          if (row === n.r1 && col === n.c1) return
          const key = ref(row, col)
          const cur = target.cells[key]
          if (cur?.s) target.cells[key] = { s: cur.s }
          else delete target.cells[key]
        })
      })
      get().select({ row: n.r1, col: n.c1 })
    },

    unmergeRange: (sheetId, r, group) => {
      mutate('병합 해제', group, (wb) => {
        const sheet = findSheet(wb, sheetId)
        const n = normalizeRange(r)
        sheet.merges = (sheet.merges ?? []).filter((m) => !rangesIntersect(m, n))
      })
    },

    insertRows: (sheetId, at, count, group) => {
      mutate(`${count}행 삽입`, group, (wb) => {
        const sheet = findSheet(wb, sheetId)
        const next: Record<string, Cell> = {}
        eachCellEntry(sheet, (row, col, cell) => {
          next[ref(row >= at ? row + count : row, col)] = cell
        })
        sheet.cells = next
        const heights: Record<number, number> = {}
        for (const [k, v] of Object.entries(sheet.rowHeights)) {
          const n = Number(k)
          heights[n >= at ? n + count : n] = v
        }
        sheet.rowHeights = heights
        sheet.rows += count
        shiftMergesOnInsert(sheet, 'row', at, count)
        remapAllFormulas(wb, (f) => shiftFormula(f, { at, delta: count, axis: 'row' }))
      })
    },

    deleteRows: (sheetId, at, count, group) => {
      mutate(`${count}행 삭제`, group, (wb) => {
        const sheet = findSheet(wb, sheetId)
        const next: Record<string, Cell> = {}
        eachCellEntry(sheet, (row, col, cell) => {
          if (row >= at && row < at + count) return
          next[ref(row >= at + count ? row - count : row, col)] = cell
        })
        sheet.cells = next
        const heights: Record<number, number> = {}
        for (const [k, v] of Object.entries(sheet.rowHeights)) {
          const n = Number(k)
          if (n >= at && n < at + count) continue
          heights[n >= at + count ? n - count : n] = v
        }
        sheet.rowHeights = heights
        sheet.rows = Math.max(1, sheet.rows - count)
        shiftMergesOnDelete(sheet, 'row', at, count)
        remapAllFormulas(wb, (f) => shiftFormula(f, { at: at + count, delta: -count, axis: 'row' }))
      })
    },

    insertCols: (sheetId, at, count, group) => {
      mutate(`${count}열 삽입`, group, (wb) => {
        const sheet = findSheet(wb, sheetId)
        const next: Record<string, Cell> = {}
        eachCellEntry(sheet, (row, col, cell) => {
          next[ref(row, col >= at ? col + count : col)] = cell
        })
        sheet.cells = next
        const widths: Record<number, number> = {}
        for (const [k, v] of Object.entries(sheet.colWidths)) {
          const n = Number(k)
          widths[n >= at ? n + count : n] = v
        }
        sheet.colWidths = widths
        sheet.cols += count
        shiftMergesOnInsert(sheet, 'col', at, count)
        remapAllFormulas(wb, (f) => shiftFormula(f, { at, delta: count, axis: 'col' }))
      })
    },

    deleteCols: (sheetId, at, count, group) => {
      mutate(`${count}열 삭제`, group, (wb) => {
        const sheet = findSheet(wb, sheetId)
        const next: Record<string, Cell> = {}
        eachCellEntry(sheet, (row, col, cell) => {
          if (col >= at && col < at + count) return
          next[ref(row, col >= at + count ? col - count : col)] = cell
        })
        sheet.cells = next
        const widths: Record<number, number> = {}
        for (const [k, v] of Object.entries(sheet.colWidths)) {
          const n = Number(k)
          if (n >= at && n < at + count) continue
          widths[n >= at + count ? n - count : n] = v
        }
        sheet.colWidths = widths
        sheet.cols = Math.max(1, sheet.cols - count)
        shiftMergesOnDelete(sheet, 'col', at, count)
        remapAllFormulas(wb, (f) => shiftFormula(f, { at: at + count, delta: -count, axis: 'col' }))
      })
    },

    sortRange: (sheetId, r, byCol, order, hasHeader, group) => {
      mutate('정렬', group, (wb) => {
        const sheet = findSheet(wb, sheetId)
        const n = normalizeRange(r)
        const start = hasHeader ? n.r1 + 1 : n.r1
        if (start > n.r2) return

        const evalr = createEvaluator(wb.sheets, sheet.name)
        const rows: Array<{ src: number; key: any; cells: Array<Cell | undefined> }> = []
        for (let row = start; row <= n.r2; row++) {
          const cells: Array<Cell | undefined> = []
          for (let col = n.c1; col <= n.c2; col++) cells.push(sheet.cells[ref(row, col)])
          rows.push({ src: row, key: evalr.raw(sheet.name, row, byCol), cells })
        }

        const dir = order === 'desc' ? -1 : 1
        rows.sort((a, b) => {
          const av = a.key
          const bv = b.key
          const aEmpty = av === null || av === undefined || av === ''
          const bEmpty = bv === null || bv === undefined || bv === ''
          if (aEmpty && bEmpty) return a.src - b.src
          if (aEmpty) return 1 // 빈 값은 항상 뒤로
          if (bEmpty) return -1
          if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir
          return String(av).localeCompare(String(bv), 'ko') * dir
        })

        // 범위 비우고 재배치 (상대참조는 이동한 만큼 오프셋)
        for (let row = start; row <= n.r2; row++)
          for (let col = n.c1; col <= n.c2; col++) delete sheet.cells[ref(row, col)]

        rows.forEach((entry, i) => {
          const dstRow = start + i
          const dRow = dstRow - entry.src
          entry.cells.forEach((cell, j) => {
            if (!cell) return
            const col = n.c1 + j
            const copy: Cell = { ...cell }
            if (copy.f) copy.f = offsetFormula(copy.f, dRow, 0)
            sheet.cells[ref(dstRow, col)] = copy
          })
        })
      })
    },

    findReplace: (sheetId, find, replace, matchCase, group) => {
      let count = 0
      mutate('찾아 바꾸기', group, (wb) => {
        const targets = sheetId ? [findSheet(wb, sheetId)] : wb.sheets
        const needle = matchCase ? find : find.toLowerCase()
        for (const sheet of targets) {
          eachCellEntry(sheet, (row, col, cell) => {
            if (cell.f) {
              const hay = matchCase ? cell.f : cell.f.toLowerCase()
              if (hay.includes(needle)) {
                cell.f = replaceAll(cell.f, find, replace, matchCase)
                count++
              }
              return
            }
            if (typeof cell.v !== 'string') return
            const hay = matchCase ? cell.v : cell.v.toLowerCase()
            if (!hay.includes(needle)) return
            const nv = replaceAll(cell.v, find, replace, matchCase)
            const parsed = parseInput(nv)
            setCell(sheet, row, col, { v: parsed.v ?? null, f: parsed.f, s: cell.s })
            count++
          })
        }
      })
      return count
    },

    setColWidth: (sheetId, col, px) => {
      mutate('열 너비', `colw:${sheetId}:${col}`, (wb) => {
        findSheet(wb, sheetId).colWidths[col] = Math.max(24, Math.round(px))
      })
    },
    setRowHeight: (sheetId, row, px) => {
      mutate('행 높이', `rowh:${sheetId}:${row}`, (wb) => {
        findSheet(wb, sheetId).rowHeights[row] = Math.max(16, Math.round(px))
      })
    },
    ensureSize: (sheetId, rows, cols) => {
      const s = get().wb.sheets.find((x) => x.id === sheetId)
      if (!s || (s.rows >= rows && s.cols >= cols)) return
      mutate('크기 확장', `grow:${sheetId}`, (wb) => {
        const sheet = findSheet(wb, sheetId)
        sheet.rows = Math.max(sheet.rows, rows)
        sheet.cols = Math.max(sheet.cols, cols)
      })
    },

    addSheet: (name, group) => {
      const existing = get().wb.sheets.map((s) => s.name)
      let n = name?.trim() || `Sheet${existing.length + 1}`
      let i = 2
      while (existing.some((x) => x.toLowerCase() === n.toLowerCase())) n = `${name || 'Sheet'}${i++}`
      const sheet = blankSheet(n)
      mutate('시트 추가', group, (wb) => {
        wb.sheets.push(sheet)
        wb.activeSheetId = sheet.id
      })
      return sheet
    },
    applyTemplate: (template, mode) => {
      let applied!: Sheet
      mutate(`템플릿 적용: ${template.name}`, undefined, (wb) => {
        if (mode === 'replace') {
          const index = wb.sheets.findIndex((s) => s.id === wb.activeSheetId)
          const current = wb.sheets[index]
          applied = { ...clone(template), id: current.id, name: current.name }
          wb.sheets[index] = applied
          return
        }

        const existing = wb.sheets.map((s) => s.name.toLowerCase())
        let name = template.name
        let suffix = 2
        while (existing.includes(name.toLowerCase())) name = `${template.name} ${suffix++}`
        applied = { ...clone(template), id: newId(), name }
        wb.sheets.push(applied)
        wb.activeSheetId = applied.id
      })
      const firstMerge = mergeAt(applied, 1, 1)
      set({
        sel: {
          sheetId: applied.id,
          anchor: { row: 1, col: 1 },
          focus: firstMerge ? { row: firstMerge.r2, col: firstMerge.c2 } : { row: 1, col: 1 },
        },
        editing: null,
      })
      return applied
    },
    renameSheet: (sheetId, name, group) => {
      mutate('시트 이름 변경', group, (wb) => {
        const s = findSheet(wb, sheetId)
        const old = s.name
        const trimmed = name.trim()
        if (!trimmed || wb.sheets.some((x) => x.id !== sheetId && x.name.toLowerCase() === trimmed.toLowerCase()))
          throw new Error(`이미 있는 시트 이름입니다: ${trimmed}`)
        s.name = trimmed
        // 다른 시트 수식의 Sheet!A1 참조도 따라간다
        const re = new RegExp(`(^|[^A-Za-z0-9_])(?:'${escapeRe(old)}'|${escapeRe(old)})!`, 'g')
        remapAllFormulas(wb, (f) => f.replace(re, (_m, p) => `${p}${quoteSheet(trimmed)}!`))
      })
    },
    deleteSheet: (sheetId, group) => {
      if (get().wb.sheets.length <= 1) throw new Error('마지막 시트는 삭제할 수 없습니다.')
      mutate('시트 삭제', group, (wb) => {
        const i = wb.sheets.findIndex((s) => s.id === sheetId)
        if (i < 0) return
        wb.sheets.splice(i, 1)
        if (wb.activeSheetId === sheetId) wb.activeSheetId = wb.sheets[Math.max(0, i - 1)].id
      })
    },
    setActiveSheet: (sheetId) => {
      const st = get()
      set({
        wb: { ...st.wb, activeSheetId: sheetId },
        sel: { sheetId, anchor: { row: 1, col: 1 }, focus: { row: 1, col: 1 } },
        editing: null,
      })
    },

    loadWorkbook: (wb, fileName) => {
      const st = get()
      const revision = st.version + 1
      set({
        wb,
        fileName,
        version: revision,
        undoStack: [...st.undoStack, { label: '파일 불러오기', wb: st.wb, sel: st.sel }].slice(-120),
        redoStack: [],
        recentChanges: [
          ...st.recentChanges,
          {
            revision,
            at: Date.now(),
            actor: 'user' as const,
            label: '파일 불러오기',
            summary: `${fileName}: ${wb.sheets.length}개 시트 불러옴`,
          },
        ].slice(-80),
        sel: { sheetId: wb.activeSheetId, anchor: { row: 1, col: 1 }, focus: { row: 1, col: 1 } },
        editing: null,
        aiTouched: {},
      })
    },

    addImage: (sheetId, image, group) => {
      mutate('이미지 추가', group, (wb) => {
        const sheet = findSheet(wb, sheetId)
        sheet.images = [...(sheet.images ?? []), image]
      })
    },
    updateImage: (sheetId, imageId, patch, group) => {
      mutate('이미지 이동/크기 조절', group, (wb) => {
        const image = findSheet(wb, sheetId).images?.find((x) => x.id === imageId)
        if (image) Object.assign(image, patch)
      })
    },
    removeImage: (sheetId, imageId, group) => {
      mutate('이미지 삭제', group, (wb) => {
        const sheet = findSheet(wb, sheetId)
        sheet.images = (sheet.images ?? []).filter((x) => x.id !== imageId)
      })
    },

    markAi: (keys) => {
      const until = Date.now() + 6000
      const next = { ...get().aiTouched }
      for (const k of keys) next[k] = until
      set({ aiTouched: next })
    },

    undo: () => {
      const st = get()
      const entry = st.undoStack[st.undoStack.length - 1]
      if (!entry) return
      const revision = st.version + 1
      set({
        wb: entry.wb,
        sel: entry.sel,
        version: revision,
        undoStack: st.undoStack.slice(0, -1),
        redoStack: [...st.redoStack, { label: entry.label, wb: st.wb, sel: st.sel }].slice(-120),
        editing: null,
        statusMsg: `되돌림: ${entry.label}`,
        recentChanges: [
          ...st.recentChanges,
          {
            revision,
            at: Date.now(),
            actor: 'user' as const,
            label: `되돌림: ${entry.label}`,
            summary: workbookChangeSummary(st.wb, entry.wb),
          },
        ].slice(-80),
      })
    },
    redo: () => {
      const st = get()
      const entry = st.redoStack[st.redoStack.length - 1]
      if (!entry) return
      const revision = st.version + 1
      set({
        wb: entry.wb,
        sel: entry.sel,
        version: revision,
        redoStack: st.redoStack.slice(0, -1),
        undoStack: [...st.undoStack, { label: entry.label, wb: st.wb, sel: st.sel }].slice(-120),
        editing: null,
        statusMsg: `다시 실행: ${entry.label}`,
        recentChanges: [
          ...st.recentChanges,
          {
            revision,
            at: Date.now(),
            actor: 'user' as const,
            label: `다시 실행: ${entry.label}`,
            summary: workbookChangeSummary(st.wb, entry.wb),
          },
        ].slice(-80),
      })
    },
  }
})

function escapeRe(s: string) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}
function quoteSheet(name: string) {
  return /^[A-Za-z_][A-Za-z0-9_]*$/.test(name) ? name : `'${name.replace(/'/g, "''")}'`
}
function replaceAll(src: string, find: string, replace: string, matchCase: boolean) {
  if (matchCase) return src.split(find).join(replace)
  return src.replace(new RegExp(escapeRe(find), 'gi'), replace)
}

// 초기 selection 을 실제 시트 id 로 맞춘다
useStore.setState((s) => ({ sel: { ...s.sel, sheetId: s.wb.activeSheetId } }))

// 평가기 메모 — version 이 바뀔 때만 재생성
let memo: { version: number; sheetName: string; ev: ReturnType<typeof createEvaluator> } | null = null
export function getEvaluator() {
  const { wb, version } = useStore.getState()
  const active = wb.sheets.find((s) => s.id === wb.activeSheetId) ?? wb.sheets[0]
  if (!memo || memo.version !== version || memo.sheetName !== active.name) {
    memo = { version, sheetName: active.name, ev: createEvaluator(wb.sheets, active.name) }
  }
  return memo.ev
}

export function colLabel(col: number) {
  return colName(col)
}
export function colWidthOf(sheet: Sheet, col: number) {
  return sheet.colWidths[col] ?? DEFAULT_COL_WIDTH
}
export function rowHeightOf(sheet: Sheet, row: number) {
  return sheet.rowHeights[row] ?? DEFAULT_ROW_HEIGHT
}
